import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { GridCustomColDef } from '../../../types';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { BasicDataGrid } from '../../Table/BasicDataGrid';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../../utils/styleHelpers';

export interface MapMiniTableParams<T = unknown> {
  id: string;
  fetchFunction: (id: string) => Promise<T[] | undefined | void>;
  dataSubject: () => BehaviorSubject<T[]>;
  loadingSubject: () => BehaviorSubject<boolean>;
  type?: string;
  tableCols: GridCustomColDef[];
  exclude: string[];
}

export const MapMiniTable = <T extends { id?: string } = { id?: string }>({
  id,
  fetchFunction,
  dataSubject,
  loadingSubject,
  tableCols,
  exclude
}: MapMiniTableParams<T>) => {
  const [mapData, setMapData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const dataSub = dataSubject().subscribe((d) => setMapData(d));
    const loadingSub = loadingSubject().subscribe((l) => setLoading(l));

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (id) fetchFunction(id);
  }, [id]);

  useEffect(() => {
    setMapData(mapData.filter((i) => !exclude.includes((i.id as string) || '')));
  }, [exclude.length, mapData.length]);

  return (
    <div
      className="w-full"
      data-testid="map-mini-table-container">
      {loading && <ComponentSpinner label="Loading Map Data" />}
      {!loading && (
        <BasicDataGrid
          sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
          className="flex"
          rows={mapData}
          columns={tableCols}
          dataTestId="map-mini-data-grid"
        />
      )}
    </div>
  );
};
